/**
 * Scroll Slider
 *
 * Desktop: the track is N × 100vh tall. A sticky panel stays at top: 0 while
 * scroll position drives a live Y-axis translateY on each __slide-panel.
 * Formula: translateY = (panelIndex - raw) × 100%, where raw goes 0 → N-1.
 * This makes each image slide in from below and out to the top in real time.
 *
 * Arrows navigate between images within the current slide's panel (crossfade).
 * Dots scroll the page to the corresponding slide position.
 *
 * Mobile: scroll-driven behaviour is disabled. All slides stack vertically.
 */
export function initScrollSlider() {
  var sliders = document.querySelectorAll('.block-scroll-slider');

  sliders.forEach(function(slider) {
    var track = slider.querySelector('.block-scroll-slider__track');
    var textSlides = slider.querySelectorAll('.block-scroll-slider__slide');
    var panels = slider.querySelectorAll('.block-scroll-slider__slide-panel');
    var prevBtn = slider.querySelector('.block-scroll-slider__prev');
    var nextBtn = slider.querySelector('.block-scroll-slider__next');
    var slideCount = panels.length;

    if (!track || slideCount < 1) return;

    var sticky = slider.querySelector('.block-scroll-slider__sticky');
    var dotsContainer = slider.querySelector('.block-scroll-slider__dots');
    var currentSlide = 0;

    // Per-slide image index (which image is active within that slide's panel)
    var imgIndices = [];
    for (var s = 0; s < slideCount; s++) imgIndices.push(0);

    function isMobile() {
      return window.innerWidth < 768;
    }

    // ── Set initial panel positions (JS overrides CSS defaults) ──────
    function initPanels() {
      panels.forEach(function(panel, i) {
        panel.style.transform = 'translateY(' + (i === 0 ? 0 : 100) + '%)';
      });
    }

    // ── Scroll-driven Y-axis update ──────────────────────────────────
    function updatePanels() {
      if (isMobile() || slideCount < 2) return;

      var panelHeight = sticky ? sticky.offsetHeight : window.innerHeight;
      var topOffset = sticky ? Math.max(0, (window.innerHeight - panelHeight) / 2) : 0;
      var trackTop = track.getBoundingClientRect().top;
      var scrollRange = track.offsetHeight - panelHeight;
      if (scrollRange <= 0) return;

      var scrolled = -trackTop + topOffset;
      var progress = Math.max(0, Math.min(1, scrolled / scrollRange));
      var raw = progress * (slideCount - 1); // 0 → slideCount-1

      // Each panel's translateY: panel i is at (i - raw) × 100%
      // When raw === i the panel is at 0% (fully visible).
      panels.forEach(function(panel, i) {
        panel.style.transform = 'translateY(' + ((i - raw) * 100).toFixed(3) + '%)';
      });

      // Update active text slide based on which panel is most centred
      var activeIndex = Math.min(slideCount - 1, Math.round(raw));
      if (activeIndex !== currentSlide) {
        activateText(currentSlide, activeIndex);
        currentSlide = activeIndex;
      }
    }

    // ── Left column text crossfade ───────────────────────────────────
    function activateText(from, to) {
      if (textSlides[from]) {
        textSlides[from].classList.remove('is-active');
        textSlides[from].setAttribute('aria-hidden', 'true');
      }
      if (textSlides[to]) {
        textSlides[to].classList.add('is-active');
        textSlides[to].setAttribute('aria-hidden', 'false');
      }
      buildDots(to);
    }

    // ── Build dots for the active slide's image count ────────────────
    function buildDots(slideIdx) {
      if (!dotsContainer) return;
      var panel = panels[slideIdx];
      var wraps = panel ? panel.querySelectorAll('.block-scroll-slider__image-wrap') : [];
      dotsContainer.innerHTML = '';
      if (wraps.length < 2) return; // no dots needed for a single image
      Array.from(wraps).forEach(function(_, i) {
        var btn = document.createElement('button');
        var active = i === imgIndices[slideIdx];
        btn.className = 'block-scroll-slider__dot' + (active ? ' is-active' : '');
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
        btn.setAttribute('aria-label', 'Image ' + (i + 1));
        btn.addEventListener('click', function() { activateImage(slideIdx, i); });
        dotsContainer.appendChild(btn);
      });
    }

    // ── Sync dot active state after an image change ──────────────────
    function updateDots(slideIdx, newImgIdx) {
      if (!dotsContainer) return;
      var btns = dotsContainer.querySelectorAll('.block-scroll-slider__dot');
      btns.forEach(function(btn, i) {
        var active = i === newImgIdx;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    // ── Scroll page to align a specific slide ────────────────────────
    function scrollToSlide(index) {
      if (isMobile() || slideCount < 2) return;
      index = Math.max(0, Math.min(slideCount - 1, index));
      var panelHeight = sticky ? sticky.offsetHeight : window.innerHeight;
      var topOffset = sticky ? Math.max(0, (window.innerHeight - panelHeight) / 2) : 0;
      var trackTop = track.getBoundingClientRect().top + window.scrollY;
      var scrollRange = track.offsetHeight - panelHeight;
      var target = trackTop - topOffset + (index / (slideCount - 1)) * scrollRange;
      window.scrollTo({ top: target, behavior: 'smooth' });
    }

    // ── Arrow / dot: navigate images within the current slide's panel ─
    // Uses inline styles exclusively for transforms/transitions to avoid CSS
    // class-order conflicts and the snap-back ghost-slide bug.
    var imgCleanupTimer = null;

    function activateImage(slideIdx, newImgIdx, direction) {
      var panel = panels[slideIdx];
      if (!panel) return;
      var wraps = panel.querySelectorAll('.block-scroll-slider__image-wrap');
      if (wraps.length < 2) return;
      newImgIdx = ((newImgIdx % wraps.length) + wraps.length) % wraps.length;
      if (newImgIdx === imgIndices[slideIdx]) return;

      // Arrow clicks pass explicit direction so loops don't invert the animation.
      var goingForward = direction !== undefined
        ? direction > 0
        : newImgIdx > imgIndices[slideIdx];

      var oldWrap = wraps[imgIndices[slideIdx]];
      var newWrap = wraps[newImgIdx];

      // Cancel any pending cleanup from an interrupted previous animation.
      clearTimeout(imgCleanupTimer);

      // 1. Snap new wrap to its entry position instantly (no transition).
      newWrap.style.transition = 'none';
      newWrap.style.transform = goingForward ? 'translateX(100%)' : 'translateX(-100%)';
      void newWrap.getBoundingClientRect(); // force layout commit

      // 2. Outgoing image on top so it slides visibly out over the incoming.
      oldWrap.style.zIndex = 2;
      newWrap.style.zIndex = 1;

      // 3. Animate both wraps.
      var dur = 'transform 0.45s ease';
      oldWrap.style.transition = dur;
      newWrap.style.transition = dur;
      oldWrap.style.transform = goingForward ? 'translateX(-100%)' : 'translateX(100%)';
      newWrap.style.transform = 'translateX(0)';

      imgIndices[slideIdx] = newImgIdx;
      updateDots(slideIdx, newImgIdx);

      // 4. After animation: freeze old wrap's transition BEFORE clearing its
      //    inline transform so it doesn't animate back across the panel.
      //    Then hand ownership back to CSS classes (is-active).
      imgCleanupTimer = setTimeout(function() {
        oldWrap.style.transition = 'none'; // freeze — inline beats any CSS class
        oldWrap.classList.remove('is-active');
        newWrap.classList.add('is-active');
        // Clear inline transforms — CSS is-active gives newWrap translateX(0),
        // default CSS gives oldWrap translateX(100%): both correct, no animation.
        oldWrap.style.transform = '';
        newWrap.style.transform = '';
        oldWrap.style.zIndex = '';
        newWrap.style.zIndex = '';
        // Re-enable transition after the browser has painted the new positions.
        requestAnimationFrame(function() {
          requestAnimationFrame(function() {
            oldWrap.style.transition = '';
          });
        });
      }, 460);
    }

    // ── Mobile carousel: per-slide image index ───────────────────────
    var mobileImgIndices = [];
    for (var ms = 0; ms < textSlides.length; ms++) mobileImgIndices.push(0);

    var mobileCleanupTimers = [];
    for (var mt = 0; mt < textSlides.length; mt++) mobileCleanupTimers.push(null);

    function activateMobileImage(slideIdx, newImgIdx, direction) {
      var slideEl = textSlides[slideIdx];
      if (!slideEl) return;
      var gallery = slideEl.querySelector('.block-scroll-slider__slide-image.has-gallery');
      if (!gallery) return;
      var wraps = gallery.querySelectorAll('.block-scroll-slider__image-wrap');
      if (wraps.length < 2) return;
      newImgIdx = ((newImgIdx % wraps.length) + wraps.length) % wraps.length;
      if (newImgIdx === mobileImgIndices[slideIdx]) return;

      var goingForward = direction !== undefined
        ? direction > 0
        : newImgIdx > mobileImgIndices[slideIdx];

      var oldWrap = wraps[mobileImgIndices[slideIdx]];
      var newWrap = wraps[newImgIdx];

      clearTimeout(mobileCleanupTimers[slideIdx]);

      newWrap.style.transition = 'none';
      newWrap.style.transform = goingForward ? 'translateX(100%)' : 'translateX(-100%)';
      void newWrap.getBoundingClientRect();

      oldWrap.style.zIndex = 2;
      newWrap.style.zIndex = 1;

      var dur = 'transform 0.45s ease';
      oldWrap.style.transition = dur;
      newWrap.style.transition = dur;
      oldWrap.style.transform = goingForward ? 'translateX(-100%)' : 'translateX(100%)';
      newWrap.style.transform = 'translateX(0)';

      mobileImgIndices[slideIdx] = newImgIdx;
      updateMobileDots(slideIdx, newImgIdx);

      mobileCleanupTimers[slideIdx] = setTimeout(function() {
        oldWrap.style.transition = 'none';
        oldWrap.classList.remove('is-active');
        newWrap.classList.add('is-active');
        oldWrap.style.transform = '';
        newWrap.style.transform = '';
        oldWrap.style.zIndex = '';
        newWrap.style.zIndex = '';
        requestAnimationFrame(function() {
          requestAnimationFrame(function() {
            oldWrap.style.transition = '';
          });
        });
      }, 460);
    }

    function buildMobileDots(slideIdx) {
      var dotsEl = slider.querySelector('.block-scroll-slider__mobile-dots[data-slide="' + slideIdx + '"]');
      if (!dotsEl) return;
      var slideEl = textSlides[slideIdx];
      if (!slideEl) return;
      var gallery = slideEl.querySelector('.block-scroll-slider__slide-image.has-gallery');
      if (!gallery) return;
      var wraps = gallery.querySelectorAll('.block-scroll-slider__image-wrap');
      dotsEl.innerHTML = '';
      Array.from(wraps).forEach(function(_, i) {
        var btn = document.createElement('button');
        var active = i === mobileImgIndices[slideIdx];
        btn.className = 'block-scroll-slider__dot' + (active ? ' is-active' : '');
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
        btn.setAttribute('aria-label', 'Image ' + (i + 1));
        btn.addEventListener('click', function() { activateMobileImage(slideIdx, i); });
        dotsEl.appendChild(btn);
      });
    }

    function updateMobileDots(slideIdx, newImgIdx) {
      var dotsEl = slider.querySelector('.block-scroll-slider__mobile-dots[data-slide="' + slideIdx + '"]');
      if (!dotsEl) return;
      var btns = dotsEl.querySelectorAll('.block-scroll-slider__dot');
      btns.forEach(function(btn, i) {
        var active = i === newImgIdx;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    function addMobileSwipe(slideIdx) {
      var slideEl = textSlides[slideIdx];
      if (!slideEl) return;
      var gallery = slideEl.querySelector('.block-scroll-slider__slide-image.has-gallery');
      if (!gallery) return;

      var touchStartX = 0;
      gallery.addEventListener('touchstart', function(e) {
        touchStartX = e.touches[0].clientX;
      }, { passive: true });
      gallery.addEventListener('touchend', function(e) {
        var dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) < 40) return;
        if (dx < 0) {
          activateMobileImage(slideIdx, mobileImgIndices[slideIdx] + 1, 1);
        } else {
          activateMobileImage(slideIdx, mobileImgIndices[slideIdx] - 1, -1);
        }
      }, { passive: true });
    }

    // ── Event listeners ──────────────────────────────────────────────
    window.addEventListener('scroll', updatePanels, { passive: true });

    if (prevBtn) prevBtn.addEventListener('click', function() {
      activateImage(currentSlide, imgIndices[currentSlide] - 1, -1);
    });
    if (nextBtn) nextBtn.addEventListener('click', function() {
      activateImage(currentSlide, imgIndices[currentSlide] + 1, 1);
    });

    // ── Init ─────────────────────────────────────────────────────────
    initPanels();
    updatePanels();
    buildDots(0);

    // Mobile carousel init
    for (var si = 0; si < textSlides.length; si++) {
      buildMobileDots(si);
      addMobileSwipe(si);
    }
  });
}
